<template>
  <div class="credit-table" :class="{ 'credit-table-dark': isDarkMode }">
    <vue-good-table
      styleClass="vgt-table condensed"
      v-bind="tableOptions"
      :columns="columns"
      :rows="rows"
      :pagination-options="creditPagination"
      :enable-row-expand="true"
      expanded-row-classes="credit-expand"
    >
      <template #table-row="props">
        <div v-if="props.column.field === 'date'" class="d-flex align-items-center gap-2">
          <span
            class="dir-dot"
            :class="props.row.ChangeType == 1 ? 'dir-in' : 'dir-out'"
            :title="props.row.ChangeType == 1 ? 'Příchozí' : 'Odchozí'"
          />
          <div class="d-flex flex-column">
            <span class="row-main">{{ props.row.CreditDate }}</span>
            <span v-if="props.row.TransactionDate" class="row-sub">{{ props.row.TransactionDate }}</span>
          </div>
        </div>
        <div v-else-if="props.column.field === 'description'" class="d-flex align-items-start gap-2">
          <div v-if="visibleKinds(props.row).length" class="kind-badges">
            <span
              v-for="kind in visibleKinds(props.row)"
              :key="kind"
              class="kind-badge"
              :class="'kind-' + kind"
            >{{ KIND_LABEL[kind] }}</span>
          </div>
          <div class="d-flex flex-column min-w-0">
            <span class="row-main">
              {{ props.row.CreditDesc }}<span v-if="isPendingWithdraw(props.row)" class="row-pending"> (zatím nevyřízená žádost)</span>
            </span>
            <span v-if="props.row.CreditMessage" class="row-sub">{{ props.row.CreditMessage }}</span>
          </div>
        </div>
        <div v-else-if="props.column.field === 'amount'" class="text-end">
          <span
            class="row-main"
            :class="props.row.ChangeType == 1 ? 'text-success' : 'text-danger'"
          >
            {{ amountLabel(props.row) }}
          </span>
          <span class="row-ccy">{{ props.row.CreditCurrency }}</span>
        </div>
        <div v-else-if="props.column.field === 'balance'" class="text-end">
          <span class="row-main row-balance">{{ formatTableNumber(props.row.CreditBalance) }}</span>
          <span class="row-ccy">{{ props.row.CreditCurrency }}</span>
        </div>
        <span v-else>{{ props.formattedRow[props.column.field] }}</span>
      </template>

      <template #row-details="props">
        <div class="credit-details">
          <div v-for="creditDetail in props.row.I" :key="creditDetail.K" class="credit-detail-row">
            <span class="row-sub">{{ creditDetail.K }}</span>
            <span class="row-main">{{ creditDetail.V }}</span>
          </div>
        </div>
      </template>

      <template #emptystate>
        <div class="text-center py-3">
          <span>Žádné záznamy v tomto filtru</span>
        </div>
      </template>
    </vue-good-table>
  </div>
</template>

<script setup>
import { reactive, computed } from "vue";
import { useStore } from "vuex";
import { tableOptions } from "../../../../helpers/tableOptions";

const props = defineProps({
  rows: Array,
  source: {
    type: String,
    default: "all",
  },
});

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);

const BANK_TYPES = new Set(["CT_IBA", "CT_OBA"]);
const GAME_TYPES = new Set(["CT_OG", "CT_CRG", "CT_CNG", "CBT_OG", "CBT_CNG"]);
const KIND_LABEL = {
  bank: "Banka",
  game: "Hry",
  credit: "Kredit",
  bonus: "Bonus",
};

const rowKinds = (row) => {
  const code = String(row.CreditType || "").toUpperCase();
  const kind = String(row.CreditKind || "").toUpperCase();
  const kinds = [];
  if (kind === "C") kinds.push("credit");
  if (kind === "B") kinds.push("bonus");
  if (BANK_TYPES.has(code)) kinds.push("bank");
  if (GAME_TYPES.has(code)) kinds.push("game");
  return kinds;
};

const visibleKinds = (row) => {
  const kinds = rowKinds(row);
  if (props.source === "all") return kinds;
  return kinds.filter((kind) => kind !== props.source);
};

const isPendingWithdraw = (row) => {
  if (String(row.CreditType || "").toUpperCase() !== "CT_OBA") return false;
  const context = row.ContextID;
  if (context === null || context === undefined || context === "") return true;
  return Number(context) === 1005;
};

const formatTableNumber = (number) => store.getters.formatTableNumber(number);

const creditPagination = computed(() => {
  const count = Array.isArray(props.rows) ? props.rows.length : 0;
  return {
    ...tableOptions.paginationOptions,
    enabled: count > 12,
    perPage: 12,
    perPageDropdown: [12, 25],
    dropdownAllowAll: true,
  };
});

const amountLabel = (row) => store.getters.formatChangeTypeNumber(row.CreditAmount);

const columns = reactive([
  {
    label: "Datum",
    field: "date",
    thClass: "text-uppercase text-xxs font-weight-bolder",
    tdClass: "text-sm"
  },
  {
    label: "Popis",
    field: "description"
  },
  {
    label: "Částka",
    field: "amount",
    thClass: "text-end text-uppercase text-xxs font-weight-bolder",
    tdClass: "text-sm text-end"
  },
  {
    label: "Zůstatek",
    field: "balance",
    thClass: "text-end text-uppercase text-xxs font-weight-bolder",
    tdClass: "text-sm text-end"
  }
]);
</script>

<style scoped>
.credit-table :deep(.vgt-table td) {
  padding-top: 0.7rem !important;
  padding-bottom: 0.7rem !important;
  vertical-align: middle;
}

.dir-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.dir-in {
  background: #2dce89;
}

.dir-out {
  background: #f5365c;
}

.kind-badges {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.35rem;
  flex-shrink: 0;
}

.kind-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 4.6em;
  box-sizing: border-box;
  font-size: 0.65rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  line-height: 1.3;
  text-align: center;
}

.kind-bank {
  background: #38BDF8;
  color: #082F49;
}

.kind-game {
  background: #FF6B21;
  color: #FFFFFF;
}

.kind-credit {
  background: #64748B;
  color: #FFFFFF;
  box-shadow: none;
}

.kind-bonus {
  background: #E2E8F0;
  color: #CA8A04;
}

.credit-table-dark .kind-bank {
  background: #0EA5E9;
  color: #F0F9FF;
}

.credit-table-dark .kind-game {
  background: #FF8A4C;
  color: #0F172A;
}

.credit-table-dark .kind-credit {
  background: #334155;
  color: #FFFFFF;
}

.credit-table-dark .kind-bonus {
  background: #334155;
  color: #FACC15;
}

.row-main {
  font-size: 0.9rem;
  font-weight: 700;
  color: #0F172A;
}

.row-sub,
.row-ccy,
.row-pending {
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748B;
}

.row-ccy {
  margin-left: 0.25rem;
}

.row-balance {
  font-weight: 600;
  color: #475569;
}

.credit-details {
  padding: 0.75rem 1rem 1rem 2rem;
}

.credit-detail-row {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.35rem;
}

.credit-table-dark :deep(.vgt-inner-wrap),
.credit-table-dark :deep(.vgt-responsive),
.credit-table-dark :deep(.vgt-wrap),
.credit-table-dark :deep(table.vgt-table) {
  background: transparent !important;
  background-color: transparent !important;
  box-shadow: none !important;
}

.credit-table-dark :deep(.vgt-table) {
  background: transparent !important;
  background-color: transparent !important;
  color: #E2E8F0 !important;
  border: none !important;
}

.credit-table-dark :deep(.vgt-table th) {
  background: #1E293B !important;
  color: #94A3B8 !important;
  border-color: #334155 !important;
}

.credit-table-dark :deep(.vgt-table td) {
  background: transparent !important;
  color: #E2E8F0 !important;
  border-color: #334155 !important;
}

.credit-table-dark :deep(.vgt-table tbody tr:hover) {
  background-color: #1E293B !important;
}

.credit-table-dark :deep(.vgt-table th.sortable button),
.credit-table-dark :deep(.vgt-table th.sortable span) {
  color: #94A3B8 !important;
}

.credit-table-dark .row-main {
  color: #E2E8F0;
}

.credit-table-dark .row-sub,
.credit-table-dark .row-ccy,
.credit-table-dark .row-balance,
.credit-table-dark .row-pending {
  color: #94A3B8;
}

.credit-table-dark :deep(.credit-expand),
.credit-table-dark :deep(.bg-gray-200) {
  background: #1E293B !important;
}

.credit-table:not(.credit-table-dark) :deep(.vgt-wrap__footer) {
  background: #F8FAFC !important;
  color: #64748B !important;
  border: none !important;
  border-radius: 0 0 0.75rem 0.75rem;
}

.credit-table:not(.credit-table-dark) :deep(.footer__row-count__select) {
  background: #FFFFFF;
  color: #0F172A;
  border: 1px solid #E2E8F0;
  border-radius: 0.5rem;
  padding: 0.2rem 0.55rem;
  font-weight: 700;
  outline: none;
}

.credit-table-dark :deep(.vgt-wrap__footer) {
  background: transparent !important;
  color: #F1F5F9 !important;
  border: none !important;
}

.credit-table-dark :deep(.footer__row-count__label),
.credit-table-dark :deep(.footer__navigation__info),
.credit-table-dark :deep(.footer__navigation__page-info),
.credit-table-dark :deep(.vgt-wrap__footer .footer__navigation__page-btn) {
  color: #F8FAFC !important;
}

.credit-table-dark :deep(.footer__row-count__select) {
  background: #1E293B !important;
  color: #FFFFFF !important;
  border: 1px solid #94A3B8 !important;
  border-radius: 0.5rem;
  padding: 0.2rem 0.55rem;
  font-weight: 800;
  outline: none;
}

.credit-table-dark :deep(.footer__row-count__select option) {
  background: #1E293B;
  color: #E2E8F0;
  font-weight: 600;
}

.credit-table-dark :deep(.footer__row-count__select option[value="12"]),
.credit-table-dark :deep(.footer__row-count__select option:checked) {
  color: #FFFFFF;
  font-weight: 800;
}
</style>
